import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { Eyebrow } from "../components/Buttons";
import { Stagger } from "../components/Reveal";
import { fadeUp, EASE } from "../motion";
import "./Gallery.css";

// Photos live in public/no1/. Edit the `alt` text to describe each one
// (it's read by screen readers and Google Images).
const base = process.env.PUBLIC_URL + "/no1/";
const images = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({
  src: `${base}${n}.png`,
  alt: `Nail design by No. 1 Nails & Spa Manly — photo ${n}`,
}));

const tile = {
  hidden: { opacity: 0, scale: 0.92, y: 20 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASE },
  },
};

export default function Gallery() {
  const [index, setIndex] = useState(null); // open photo, or null
  const [dir, setDir] = useState(0); // slide direction for the lightbox
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const step = useCallback((d) => {
    setDir(d);
    setIndex((i) => (i + d + images.length) % images.length);
  }, []);

  // Keyboard controls + lock page scroll while the lightbox is open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, step]);

  return (
    <section className="gallery section section--alt" id="gallery">
      <div className="container">
        <Stagger as="header" className="gallery__head">
          <motion.div variants={fadeUp}>
            <Eyebrow>SELECTED WORK</Eyebrow>
          </motion.div>
          <motion.h2 variants={fadeUp}>From the salon</motion.h2>
          <motion.p variants={fadeUp} className="gallery__sub">
            A small sample of recent work. For the full gallery, visit us in
            Manly.
          </motion.p>
        </Stagger>

        <Stagger
          className="gallery__grid gallery__grid--photos"
          gap={0.07}
          amount={0.1}
        >
          {images.map((img, i) => (
            <motion.button
              key={img.src}
              type="button"
              className="gphoto"
              variants={tile}
              whileHover="hover"
              onClick={() => {
                setDir(0);
                setIndex(i);
              }}
              aria-label={`Open photo ${i + 1} of ${images.length}`}
            >
              <motion.img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                draggable="false"
                variants={{ hover: { scale: 1.06 } }}
                transition={{ duration: 0.6, ease: EASE }}
              />
              <motion.span
                className="gphoto__overlay"
                initial={{ opacity: 0 }}
                variants={{ hover: { opacity: 1 } }}
                transition={{ duration: 0.3 }}
              >
                <ZoomIn aria-hidden="true" />
              </motion.span>
            </motion.button>
          ))}
        </Stagger>
      </div>

      {/* ---------- Lightbox (rendered into <body> so it always covers the screen) ---------- */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              className="lightbox"
              role="dialog"
              aria-modal="true"
              aria-label="Photo viewer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={close}
            >
              <button
                className="lightbox__close"
                onClick={close}
                aria-label="Close"
              >
                <X />
              </button>

              <div
                className="lightbox__stage"
                onClick={(e) => e.stopPropagation()}
              >
                <AnimatePresence initial={false} custom={dir} mode="popLayout">
                  <motion.img
                    key={index}
                    src={images[index].src}
                    alt={images[index].alt}
                    className="lightbox__img"
                    custom={dir}
                    draggable="false"
                    initial={(d) => ({
                      opacity: 0,
                      x: d * 80,
                      scale: d ? 1 : 0.92,
                    })}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={(d) => ({ opacity: 0, x: d * -80 })}
                    transition={{ duration: 0.4, ease: EASE }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.6}
                    onDragEnd={(_, info) => {
                      if (info.offset.x < -60) step(1);
                      else if (info.offset.x > 60) step(-1);
                    }}
                  />
                </AnimatePresence>
              </div>

              <button
                className="lightbox__nav lightbox__nav--prev"
                aria-label="Previous photo"
                onClick={(e) => {
                  e.stopPropagation();
                  step(-1);
                }}
              >
                <ChevronLeft />
              </button>
              <button
                className="lightbox__nav lightbox__nav--next"
                aria-label="Next photo"
                onClick={(e) => {
                  e.stopPropagation();
                  step(1);
                }}
              >
                <ChevronRight />
              </button>

              <div className="lightbox__count">
                {index + 1} / {images.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </section>
  );
}
